import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  Grid,
  ButtonGroup,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  ClickAwayListener,
  Tooltip,
  ButtonProps, Theme,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import Button from './Button';
import { useId } from '../../hooks';

/* eslint-disable @typescript-eslint/no-explicit-any, react/no-array-index-key */
const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  tooltip: {
    backgroundColor: '#515B6A',
    fontSize: 12,
    maxWidth: 150,
  },
  selectButton: {
    width: '100%',
  },
  buttonGroup: {
    width: 140,
  },
  buttonText: {
    padding: '0 12px',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  list: {
    maxHeight: '50vh',
    overflowY: 'auto',
  },
}), { name: 'ButtonGroup' });

export type ButtonOption = {
  title: React.ReactNode;
  titleList?: React.ReactNode;
  tooltip?: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => void;
  disabled: boolean;
  [key: string]: any;
};

type Props = ButtonProps & {
  buttons: ButtonOption[];
  currentIndex: number;
  selectedDefault?: () => number;
  buttonClassName?: string;
  overlay?: boolean;
};

const ButtonGroupSelect = ({
  buttons,
  currentIndex = 0,
  selectedDefault,
  variant = 'contained',
  color = 'primary',
  overlay = true,
  ...props
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const menuId = useId();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const element = listRef.current;
    if (element !== null) {
      const focusItem = (focusNext = true) => {
        const items = element.querySelectorAll('li');
        const range = items.length;
        let currentFocus = 0;

        // find current focused item
        items.forEach((item, key) => {
          if (item.tabIndex === 0) currentFocus = key;
        });
        if (!focusNext) {
          currentFocus = (currentFocus + 1 >= range) ? 0 : currentFocus + 1;
        } else {
          currentFocus = (currentFocus - 1 < 0) ? range - 1 : currentFocus - 1;
        }

        // focus selected item
        items[currentFocus].focus();

        // update index & classes
        items.forEach((item, key) => {
          // eslint-disable-next-line no-param-reassign
          item.tabIndex = (key === currentFocus) ? 0 : -1;
          if (key !== currentFocus) {
            item.classList.remove('Mui-selected');
          }
        });
      };

      switch (e.key) {
        case 'ArrowUp':
          // select up
          e.preventDefault();
          focusItem();
          break;
        case 'ArrowDown':
          // select down
          e.preventDefault();
          focusItem(false);
          break;
        case 'Tab':
          // select down
          e.preventDefault();
          focusItem(false);
          break;
        default:
          break;
      }
    }
  }, [listRef]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);


  const handleMenuItemClick = useCallback((
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    buttons[index].onClick(event);
    setOpen(false);
  }, [setOpen, buttons]);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, [setOpen]);

  const handleClose = useCallback((event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  }, [setOpen]);

  if (buttons === undefined || buttons.length === 0) {
    return null;
  }

  return (
    <Grid container direction="column" alignItems="center">
      <Grid item xs={12}>
        <ButtonGroup variant={variant} color={color} ref={anchorRef} style={{ width: 140 }}>
          <Button
            onClick={handleToggle}
            variant={variant}
            color={color}
            overlay={overlay}
            style={{ width: '100%', textTransform: 'none' }}
            {...props}
          >
            {buttons[currentIndex].title}
            <ArrowDropDownIcon style={{ marginLeft: 'auto' }} />
          </Button>
        </ButtonGroup>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          style={{ zIndex: 2147483100 }}
          placement="bottom-end"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom-end' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id={menuId} ref={listRef} className={classes.list}>
                    {buttons.map((item, index) => (
                      item.tooltip
                        ? (
                          <Tooltip
                            title={item.tooltip}
                            PopperProps={{ disablePortal: true }}
                            classes={{
                              tooltip: classes.tooltip,
                            }}
                            key={index}
                          >
                            <MenuItem
                              key={index}
                              selected={index === currentIndex}
                              disabled={item.disabled}
                              onClick={(event) => handleMenuItemClick(event, index)}
                            >
                              {item.title}
                            </MenuItem>
                          </Tooltip>
                        )
                        : (
                          <MenuItem
                            key={index}
                            selected={index === currentIndex}
                            disabled={item.disabled}
                            onClick={(event) => handleMenuItemClick(event, index)}
                          >
                            {item.titleList ? item.titleList : item.title}
                          </MenuItem>
                        )
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>
    </Grid>
  );
};


export default ButtonGroupSelect;
